/**
 * Typed endpoint functions.
 *
 * One place where a URL string appears. Components call these; they never build
 * a path, so renaming a route is one edit rather than a grep.
 */

import { api } from './client'
import type {
  Bootstrap, Brand, BusinessDetail, BusinessSummary, Category, CrawlDomain,
  CrawlJob, CrawlJobDetail, CrawlProgress, Dashboard, Facets, IndexStats,
  MarketRates, MarketSummary, MergedBusinessResponse, PaginationMeta, PriceHistory,
  PriceOffer, ProductDetail, ProductSummary, RelationshipGraph, ReviewItem,
  AdminArticleRow, IntakeBatch, IntakeItem, IntakeRun, PendingBusiness,
  PriceMovements,
  SearchMeta, Service, SourceRef, StateDetail, State, Suggestion, User,
  ZeroResultSearch,
} from '@/types/api'

export interface SearchParams {
  q?: string
  type_of?: 'business' | 'product' | 'service'
  state?: string
  lga?: string
  city?: string
  category?: string
  type?: string
  verified?: boolean
  has_price?: boolean
  min_price?: number
  max_price?: number
  min_trust?: number
  sort?: string
  page?: number
  per_page?: number
}

type Params = Record<string, string | number | boolean | null | undefined>

/* -- Reference ------------------------------------------------------------- */
export const referenceApi = {
  bootstrap: () => api.get<Bootstrap>('/reference/bootstrap', { authenticated: false }),
  states: () => api.get<State[]>('/locations/states', { authenticated: false }),
  state: (slug: string) =>
    api.get<StateDetail>(`/locations/states/${slug}`, { authenticated: false }),
  categories: (params?: { tree?: boolean; parent?: string; kind?: string }) =>
    api.get<Category[]>('/categories', { params: params as Params, authenticated: false }),
  category: (slug: string) =>
    api.get<Category>(`/categories/${slug}`, { authenticated: false }),
  brands: (params?: { q?: string; page?: number }) =>
    api.getWithMeta<Brand[]>('/brands', { params: params as Params, authenticated: false }),
}

/* -- Market rates ---------------------------------------------------------- */
export const marketApi = {
  /** FX, commodity benchmarks and figures derived from the index itself. */
  rates: () => api.get<MarketRates>('/markets/rates', { authenticated: false }),
}

/* -- Search ---------------------------------------------------------------- */
export const searchApi = {
  search: (params: SearchParams) =>
    api.getWithMeta<BusinessSummary[] | ProductSummary[] | Service[]>('/search', {
      params: params as Params,
      authenticated: false,
    }) as Promise<{ data: unknown[]; meta: SearchMeta }>,

  suggest: (q: string, signal?: AbortSignal) =>
    api.get<Suggestion[]>('/search/suggest', {
      params: { q },
      signal,
      authenticated: false,
    }),

  facets: () => api.get<Facets>('/search/facets', { authenticated: false }),
  stats: () => api.get<IndexStats>('/search/stats', { authenticated: false }),
}

/* -- Businesses ------------------------------------------------------------ */
export const businessApi = {
  list: (params: SearchParams) =>
    api.getWithMeta<BusinessSummary[]>('/businesses', {
      params: params as Params,
      authenticated: false,
    }),

  detail: (slug: string) =>
    api.get<BusinessDetail | MergedBusinessResponse>(`/businesses/${slug}`, {
      authenticated: false,
    }),

  products: (slug: string, page = 1) =>
    api.getWithMeta<ProductSummary[]>(`/businesses/${slug}/products`, {
      params: { page },
      authenticated: false,
    }),

  prices: (slug: string, page = 1) =>
    api.getWithMeta<PriceOffer[]>(`/businesses/${slug}/prices`, {
      params: { page },
      authenticated: false,
    }),

  services: (slug: string) =>
    api.get<Service[]>(`/businesses/${slug}/services`, { authenticated: false }),

  relationships: (slug: string) =>
    api.get<RelationshipGraph>(`/businesses/${slug}/relationships`, {
      authenticated: false,
    }),

  sources: (slug: string, page = 1) =>
    api.getWithMeta<SourceRef[]>(`/businesses/${slug}/sources`, {
      params: { page },
      authenticated: false,
    }),
}

/* -- Products and prices --------------------------------------------------- */
export const productApi = {
  list: (params: SearchParams) =>
    api.getWithMeta<ProductSummary[]>('/products', {
      params: params as Params,
      authenticated: false,
    }),

  detail: (slug: string) =>
    api.get<ProductDetail>(`/products/${slug}`, { authenticated: false }),

  suppliers: (slug: string) =>
    api.get<{ suppliers: PriceOffer[]; statistics: unknown[]; counts: Record<string, number> }>(
      `/products/${slug}/suppliers`,
      { authenticated: false },
    ),

  priceHistory: (slug: string, days = 90) =>
    api.get<PriceHistory>(`/products/${slug}/price-history`, {
      params: { days },
      authenticated: false,
    }),
}

export const priceApi = {
  movements: (limit = 8) =>
    api.get<PriceMovements>('/prices/movements', { params: { limit } }),

  recent: (page = 1) =>
    api.getWithMeta<PriceOffer[]>('/prices/recent', {
      params: { page },
      authenticated: false,
    }),
  marketSummary: (currency = 'NGN') =>
    api.get<MarketSummary>('/prices/market-summary', {
      params: { currency },
      authenticated: false,
    }),
}

export const serviceApi = {
  list: (params: SearchParams) =>
    api.getWithMeta<Service[]>('/services', {
      params: params as Params,
      authenticated: false,
    }),
  detail: (uuid: string) => api.get<Service>(`/services/${uuid}`, { authenticated: false }),
}

/* -- Account --------------------------------------------------------------- */
export const accountApi = {
  me: () => api.get<User>('/auth/me'),
  changePassword: (current_password: string, new_password: string) =>
    api.post<{ message: string }>('/auth/change-password', {
      current_password,
      new_password,
    }),
  sessions: () =>
    api.get<
      Array<{
        created_at: string
        expires_at: string
        user_agent: string | null
        /** Masked at the server — the last octet is replaced with `x`. */
        ip_address?: string | null
      }>
    >('/auth/sessions'),
  revokeAllSessions: () =>
    api.post<{ message: string; revoked: number }>('/auth/sessions/revoke-all'),
}

/* -- Crawling (staff) ------------------------------------------------------ */
export interface StartCrawlInput {
  target_url: string
  crawl_type?: string
  max_pages?: number
  max_depth?: number
  priority?: number
}

export const crawlApi = {
  start: (input: StartCrawlInput) => api.post<CrawlJob>('/crawl/jobs', input),

  jobs: (params?: { status?: string; domain?: string; page?: number }) =>
    api.getWithMeta<CrawlJob[]>('/crawl/jobs', { params: params as Params }),

  job: (uuid: string) => api.get<CrawlJobDetail>(`/crawl/jobs/${uuid}`),

  /** Small by design — the admin UI polls this while a crawl runs. */
  progress: (uuid: string) => api.get<CrawlProgress>(`/crawl/jobs/${uuid}/progress`),

  pages: (uuid: string, params?: { page?: number; errors_only?: boolean; page_type?: string }) =>
    api.getWithMeta<import('@/types/api').CrawledPage[]>(`/crawl/jobs/${uuid}/pages`, {
      params: params as Params,
    }),

  events: (uuid: string, params?: { page?: number; level?: string }) =>
    api.getWithMeta<import('@/types/api').CrawlEvent[]>(`/crawl/jobs/${uuid}/events`, {
      params: params as Params,
    }),

  pause: (uuid: string) => api.post<CrawlJob>(`/crawl/jobs/${uuid}/pause`),
  resume: (uuid: string) => api.post<CrawlJob>(`/crawl/jobs/${uuid}/resume`),
  cancel: (uuid: string) => api.post<CrawlJob>(`/crawl/jobs/${uuid}/cancel`),

  domains: (params?: { blocked?: boolean; q?: string; page?: number }) =>
    api.getWithMeta<CrawlDomain[]>('/crawl/domains', { params: params as Params }),

  blockDomain: (domain: string, reason: string) =>
    api.post<CrawlDomain>(`/crawl/domains/${domain}/block`, { reason }),
  unblockDomain: (domain: string) =>
    api.post<CrawlDomain>(`/crawl/domains/${domain}/unblock`),
}

/* -- Intake ---------------------------------------------------------------- */
export const intakeApi = {
  list: (params?: { status?: string; batch?: string; page?: number; per_page?: number }) =>
    api.getWithMeta<IntakeItem[]>('/admin/intake', { params: params as Params }),

  item: (uuid: string) => api.get<IntakeItem>(`/admin/intake/${uuid}`),

  /** Paste. The server splits newlines and commas, so a textarea can be sent raw. */
  add: (urls: string, label?: string) =>
    api.post<IntakeBatch>('/admin/intake', { urls, label }),

  importFile: (file: File, label?: string) => {
    const form = new FormData()
    form.append('file', file)
    if (label) form.append('label', label)
    return api.upload<IntakeBatch>('/admin/intake/import', form)
  },

  run: (input: { limit?: number; write_profiles?: boolean }) =>
    api.post<IntakeRun>('/admin/intake/run', input),

  retry: (uuid: string) => api.post<IntakeItem>(`/admin/intake/${uuid}/retry`),

  batches: () => api.get<IntakeBatch[]>('/admin/intake/batches'),

  /* -- Approving what the crawler found -----------------------------------
   * Intake creates every business as a draft. These are the endpoints that
   * let a person put one into the catalogue, or throw it out.
   */
  pendingBusinesses: () =>
    api.getWithMeta<PendingBusiness[]>('/admin/businesses/pending', {
      params: { per_page: 100 },
    }),

  publishBusiness: (uuid: string) =>
    api.post<PendingBusiness>(`/admin/businesses/${uuid}/publish`),

  rejectBusiness: (uuid: string) =>
    api.post<PendingBusiness>(`/admin/businesses/${uuid}/reject`),
}

/* -- Admin ----------------------------------------------------------------- */
export const adminApi = {
  dashboard: () => api.get<Dashboard>('/admin/dashboard'),

  zeroResultSearches: () =>
    api.get<ZeroResultSearch[]>('/admin/dashboard/zero-result-searches'),

  reviewQueue: (params?: { status?: string; entity_type?: string; page?: number }) =>
    api.getWithMeta<ReviewItem[]>('/admin/review-queue', { params: params as Params }),

  reviewItem: (id: number) => api.get<ReviewItem>(`/admin/review-queue/${id}`),

  decide: (
    id: number,
    input: { action: 'approve' | 'reject' | 'merge' | 'escalate'; notes?: string; merge_into_id?: number },
  ) => api.post<ReviewItem>(`/admin/review-queue/${id}/decide`, input),

  /**
   * Written profiles at every status.
   *
   * The public content endpoint refuses a status filter on purpose — one
   * forgotten authorisation check and it would serve unvalidated text — so the
   * drafts that failed validation are only reachable here.
   */
  articles: (params?: { status?: string; subject_type?: string; page?: number }) =>
    api.getWithMeta<AdminArticleRow[]>('/admin/articles', { params: params as Params }),

  publishArticle: (uuid: string) =>
    api.post<AdminArticleRow>(`/admin/articles/${uuid}/publish`),

  archiveArticle: (uuid: string) =>
    api.post<AdminArticleRow>(`/admin/articles/${uuid}/archive`),

  users: (params?: { role?: string; status?: string; q?: string; page?: number }) =>
    api.getWithMeta<User[]>('/admin/users', { params: params as Params }),

  changeUserRole: (uuid: string, role: string) =>
    api.patch<User>(`/admin/users/${uuid}/role`, { role }),

  changeUserStatus: (uuid: string, status: string) =>
    api.patch<User>(`/admin/users/${uuid}/status`, { status }),

  auditLog: (params?: { action?: string; entity_type?: string; page?: number }) =>
    api.getWithMeta<Array<Record<string, unknown>>>('/admin/audit-log', {
      params: params as Params,
    }),

  listSettings: () =>
    api.get<Array<{ key: string; value: unknown; description: string | null }>>(
      '/admin/settings',
    ),

  updateSetting: (key: string, value: unknown) =>
    api.put<{ key: string; value: unknown; description: string | null }>(
      `/admin/settings/${key}`,
      { value },
    ),
}

/* -- SEO (staff) ------------------------------------------------------------ */
export interface SeoIssue {
  id: number
  issue_type: string
  severity: string
  path: string
  entity_type: string | null
  entity_id: number | null
  message: string
  status: string
  first_seen_at: string | null
  last_seen_at: string | null
}

export interface SeoAuditRun {
  id: number
  pages_scanned: number
  indexable_pages: number
  noindex_pages: number
  issues_found: number
  issues_by_type: Record<string, number>
  average_score: number
  duration_ms: number
  created_at: string
}

export interface SeoHealth {
  last_run: SeoAuditRun | null
  score_change: number | null
  open_issues: number
  open_by_type: Record<string, number>
  open_by_severity: Record<string, number>
}

export interface SeoThresholds {
  business: { min_quality_score: number; min_sources: number }
  listing: { min_results: number }
  price: { min_observations: number }
  facets: { indexable: string[]; max_combined: number }
  never_indexed: string[]
  noindex_reasons: string[]
}

export const seoApi = {
  health: () => api.get<SeoHealth>('/seo/health'),
  runAudit: () => api.post<SeoAuditRun>('/seo/audit'),
  audits: () => api.get<SeoAuditRun[]>('/seo/audits'),
  thresholds: () => api.get<SeoThresholds>('/seo/indexability'),

  issues: (params?: { status?: string; type?: string; severity?: string; page?: number }) =>
    api.getWithMeta<SeoIssue[]>('/seo/issues', { params: params as Params }),

  ignoreIssue: (id: number) => api.post<SeoIssue>(`/seo/issues/${id}/ignore`),

  redirects: (params?: { q?: string; page?: number }) =>
    api.getWithMeta<Array<Record<string, unknown>>>('/seo/redirects', {
      params: params as Params,
    }),

  createRedirect: (input: { old_path: string; new_path: string; reason?: string }) =>
    api.post<Record<string, unknown>>('/seo/redirects', input),
}

export type { PaginationMeta }
